import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  LoginResponseSchema,
  MeResponseSchema,
  PinChangeResponseSchema,
  type Locale,
  type LoginResponse,
  type MeResponse,
} from '@saniplus/contracts';

import { getApiClientRuntime } from '../config';
import { baseApi } from '../api/baseApi';
import { isApiError, networkError, type ApiError } from '../api/apiError';
import { apiFetch } from '../api/http';
import { clearRefreshFailure, refreshAccessToken } from '../api/refresh';
import { clearSessionSnapshot, readSessionSnapshot } from './sessionSnapshot';

/**
 * Session flows are thunks, not RTK Query endpoints (PM decision, D-17
 * reconciliation). They are one-shot commands with no cache entry, they run
 * before the store has a token, and `bootstrapSession` has to sequence two
 * calls — none of which RTK Query models well. Every *data* fetch is still
 * RTK Query.
 */

function asApiError(error: unknown): ApiError {
  return isApiError(error) ? error : networkError(error);
}

export interface LoginArgs {
  readonly employeeNumber: string;
  readonly pin: string;
}

/**
 * The PIN never rides in `meta.arg`. `createAsyncThunk` always stamps the
 * exact `arg` it is called with onto `pending`/`fulfilled`/`rejected`'s
 * `meta.arg` — there is no hook to redact a field afterward, and that `meta`
 * flows through every middleware and into the Redux DevTools action log
 * verbatim. So the arg the *internal* thunk sees is never the raw PIN: it is
 * a getter closure that only the payload creator ever calls. `login` itself
 * stays a drop-in replacement for the old `AsyncThunk` — same call shape,
 * same `.pending`/`.fulfilled`/`.rejected` matchers `authSlice` and both
 * apps' login screens already use.
 */
interface LoginThunkArgs {
  readonly employeeNumber: string;
  readonly getPin: () => string;
}

const loginThunk = createAsyncThunk<LoginResponse, LoginThunkArgs, { rejectValue: ApiError }>(
  'auth/login',
  async ({ employeeNumber, getPin }, { rejectWithValue }) => {
    const runtime = getApiClientRuntime();
    try {
      const result = await apiFetch({
        url: '/auth/login',
        method: 'POST',
        body: {
          employeeNumber,
          pin: getPin(),
          deviceUuid: runtime.getDeviceUuid(),
          deviceInfo: { userAgent: runtime.getUserAgent() },
        },
      });
      // A new session exists; a cooldown armed by the pre-login refresh 401 is
      // now about the wrong cookie.
      clearRefreshFailure();
      const parsed = LoginResponseSchema.parse(result.data);
      const chosenLocale = runtime.getLocale();
      if (chosenLocale === parsed.user.locale) return parsed;
      /*
       * The login screen's language toggle only reaches i18next and
       * IndexedDB (D-13: nothing authenticated exists yet to post
       * `/me/locale` to), so a fresh account's stored locale is still
       * whatever it was created with. Left alone, `LocaleSync` reads that
       * stale value the instant this session lands and flips the screen out
       * from under a choice the person just made — most visibly on the
       * forced pin-change screen that follows a first login. Folding the
       * choice into the returned user means the store never observes the
       * stale locale to begin with.
       *
       * The push to the profile is what `mustChangePin` gates: that token is
       * the one-time-PIN one, which D-05 refuses on every route but refresh,
       * logout, `/auth/me` and the change itself — so posting here would buy
       * a guaranteed 403 in the console and nothing else. `changePin` makes
       * the same push the moment the restriction lifts.
       */
      if (!parsed.mustChangePin) {
        void apiFetch(
          { url: '/me/locale', method: 'POST', body: { locale: chosenLocale } },
          { token: parsed.accessToken },
        ).catch(() => undefined);
      }
      return { ...parsed, user: { ...parsed.user, locale: chosenLocale } };
    } catch (error) {
      return rejectWithValue(asApiError(error));
    }
  },
);

type LoginThunk = ((args: LoginArgs) => ReturnType<typeof loginThunk>) & {
  readonly pending: typeof loginThunk.pending;
  readonly fulfilled: typeof loginThunk.fulfilled;
  readonly rejected: typeof loginThunk.rejected;
  readonly typePrefix: typeof loginThunk.typePrefix;
};

export const login: LoginThunk = Object.assign(
  (args: LoginArgs) => loginThunk({ employeeNumber: args.employeeNumber, getPin: () => args.pin }),
  {
    pending: loginThunk.pending,
    fulfilled: loginThunk.fulfilled,
    rejected: loginThunk.rejected,
    typePrefix: loginThunk.typePrefix,
  },
);

/**
 * Rotate the access token through the shared in-flight refresh promise.
 *
 * The rejection carries which of the two failures happened, because the slice
 * ends the session on one and keeps it on the other: `status: 0` is the
 * unreachable server (`networkError`), anything else is a refusal.
 */
export const refreshSession = createAsyncThunk<string, void, { rejectValue: ApiError }>(
  'auth/refreshSession',
  async (_arg, { rejectWithValue }) => {
    const { token, offline } = await refreshAccessToken();
    if (token === null) {
      return rejectWithValue(
        offline
          ? networkError(new Error('the refresh could not reach the server'))
          : {
              status: 401,
              code: 'INVALID_REFRESH_TOKEN',
              message: 'session expired',
              requestId: 'local',
            },
      );
    }
    return token;
  },
);

export const logout = createAsyncThunk<void, void, { rejectValue: ApiError }>(
  'auth/logout',
  async () => {
    // A logout that cannot reach the server still ends the local session:
    // the refresh token expires on its own, and leaving the user staring at
    // an authenticated shell would be worse.
    await apiFetch({ url: '/auth/logout', method: 'POST', body: {} }).catch(() => undefined);
  },
);

export interface ChangePinArgs {
  readonly currentPin: string;
  readonly newPin: string;
}

/** Same PIN-out-of-`meta.arg` treatment as `login`, above, for both PINs. */
interface ChangePinThunkArgs {
  readonly getCurrentPin: () => string;
  readonly getNewPin: () => string;
}

/**
 * Resolves with the **replacement** access token, and the slice installs it.
 *
 * The token in hand was minted with the one-time PIN and carries `mcp: true`;
 * the API refuses every route with it (`403 PIN_CHANGE_REQUIRED`) and only a
 * 401 makes `baseQuery` refresh. Keeping the old token here is what left a
 * worker who had just chosen a PIN staring at a home screen with no context at
 * all — no shift, no supervisor — on a night they were scheduled.
 */
const changePinThunk = createAsyncThunk<string, ChangePinThunkArgs, { rejectValue: ApiError }>(
  'auth/changePin',
  async ({ getCurrentPin, getNewPin }, { getState, rejectWithValue }) => {
    const state = getState() as { auth: { accessToken: string | null; user: { locale: Locale } | null } };
    const token = state.auth.accessToken;
    try {
      const result = await apiFetch(
        {
          url: '/auth/pin/change',
          method: 'POST',
          body: { currentPin: getCurrentPin(), newPin: getNewPin() },
        },
        { token },
      );
      // Same as `login`: the session this restamps is newer than any failure
      // the cooldown is still remembering.
      clearRefreshFailure();
      const accessToken = PinChangeResponseSchema.parse(result.data).accessToken;
      /*
       * The pin-change screen's own toggle only flips i18next while
       * `mustChangePin` is set — `/me/locale` is refused there like every
       * other route (D-05). Now that the change went through and the
       * restriction is gone, catch the profile up with whatever the screen
       * is actually showing, the same way `login` does for a locale picked
       * before authentication.
       */
      const chosenLocale = getApiClientRuntime().getLocale();
      if (state.auth.user !== null && state.auth.user.locale !== chosenLocale) {
        void apiFetch(
          { url: '/me/locale', method: 'POST', body: { locale: chosenLocale } },
          { token: accessToken },
        ).catch(() => undefined);
      }
      return accessToken;
    } catch (error) {
      return rejectWithValue(asApiError(error));
    }
  },
);

type ChangePinThunk = ((args: ChangePinArgs) => ReturnType<typeof changePinThunk>) & {
  readonly pending: typeof changePinThunk.pending;
  readonly fulfilled: typeof changePinThunk.fulfilled;
  readonly rejected: typeof changePinThunk.rejected;
  readonly typePrefix: typeof changePinThunk.typePrefix;
};

export const changePin: ChangePinThunk = Object.assign(
  (args: ChangePinArgs) =>
    changePinThunk({ getCurrentPin: () => args.currentPin, getNewPin: () => args.newPin }),
  {
    pending: changePinThunk.pending,
    fulfilled: changePinThunk.fulfilled,
    rejected: changePinThunk.rejected,
    typePrefix: changePinThunk.typePrefix,
  },
);

export interface BootstrapResult {
  /**
   * `null` when the refresh could not reach the server and the shell was
   * rehydrated from the offline identity snapshot instead: unverified, and
   * holding no credential at all (`auth/sessionSnapshot.ts`).
   */
  readonly accessToken: string | null;
  readonly me: MeResponse;
}

/**
 * Page load: the access token lives only in memory, so a reload has nothing
 * but the refresh cookie. Refresh first, then read `/auth/me` — which is also
 * the only call that reports `mustChangePin` after a reload.
 *
 * When the refresh cannot reach the server at all — the installed PWA reopened
 * inside a plant, which is the normal way this app is started — the snapshot
 * stands in for `/auth/me` so the worker keeps their shell and their queued
 * punch. It is identity, never authority: the slice records it as
 * `status: 'offline'` with no token, and the next reachable refresh either
 * upgrades the session or ends it.
 */
export const bootstrapSession = createAsyncThunk<
  BootstrapResult,
  void,
  { rejectValue: ApiError }
>('auth/bootstrapSession', async (_arg, { rejectWithValue }) => {
  const { token: accessToken, offline } = await refreshAccessToken();
  if (accessToken === null) {
    if (offline) {
      const snapshot = await readSessionSnapshot();
      if (snapshot !== null) return { accessToken: null, me: snapshot };
      return rejectWithValue(
        networkError(new Error('offline, and no session was ever established on this device')),
      );
    }
    // A refusal is the session actually ending, so the snapshot goes with it:
    // otherwise the next offline start would paint a shell for a session the
    // server has already revoked.
    await clearSessionSnapshot();
    return rejectWithValue({
      status: 401,
      code: 'UNAUTHENTICATED',
      message: 'no session',
      requestId: 'local',
    });
  }
  try {
    const result = await apiFetch({ url: '/auth/me' }, { token: accessToken });
    return { accessToken, me: MeResponseSchema.parse(result.data) };
  } catch (error) {
    return rejectWithValue(asApiError(error));
  }
});

/**
 * `/me/context` is server-localized (site names, punchability guidance
 * rendered in the caller's stored language), so a locale change that leaves
 * that cache in place shows half the punch screen in the old language until
 * a punch or an unrelated refetch happens to invalidate it. Invalidating
 * `Context`/`Session` here — the same pair `profileApi.setMyLocale` (the
 * RTK-Query route through this same endpoint) invalidates — keeps both paths
 * to `/me/locale` behaving the same way.
 */
export const setUserLocale = createAsyncThunk<Locale, Locale, { rejectValue: ApiError }>(
  'auth/setUserLocale',
  async (locale, { dispatch, getState, rejectWithValue }) => {
    const token = (getState() as { auth: { accessToken: string | null } }).auth.accessToken;
    try {
      await apiFetch({ url: '/me/locale', method: 'POST', body: { locale } }, { token });
      dispatch(baseApi.util.invalidateTags(['Context', 'Session']));
      return locale;
    } catch (error) {
      return rejectWithValue(asApiError(error));
    }
  },
);
